// src/contexts/FavoritesContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Load favorites from database
  const loadFavorites = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('user_favorites')
        .select('product_id')
        .eq('user_id', user.id);

      if (error) throw error;

      const favoriteIds = new Set(data.map(item => item.product_id));
      setFavorites(favoriteIds);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Load user's favorites on mount and when user changes
  useEffect(() => {
    if (user) {
      loadFavorites();
    } else {
      setFavorites(new Set());
      setLoading(false);
    }
  }, [user, loadFavorites]);

  // Add product to favorites
  const addToFavorites = async (productId) => {
    if (!user) {
      // Handle unauthenticated user - could show login prompt
      return { success: false, message: 'يجب تسجيل الدخول أولاً' };
    }

    try {
      const { error } = await supabase
        .from('user_favorites')
        .insert([{ user_id: user.id, product_id: productId }]);

      if (error) throw error;

      setFavorites(prev => new Set([...prev, productId]));
      return { success: true };
    } catch (error) {
      console.error('Error adding to favorites:', error);
      return { success: false, message: 'حدث خطأ أثناء الإضافة للمفضلة' };
    }
  };

  // Remove product from favorites
  const removeFromFavorites = async (productId) => {
    if (!user) {
      return { success: false, message: 'يجب تسجيل الدخول أولاً' };
    }

    try {
      const { error } = await supabase
        .from('user_favorites')
        .delete()
        .eq('user_id', user.id)
        .eq('product_id', productId);

      if (error) throw error;

      setFavorites(prev => {
        const newFavorites = new Set(prev);
        newFavorites.delete(productId);
        return newFavorites;
      });
      return { success: true };
    } catch (error) {
      console.error('Error removing from favorites:', error);
      return { success: false, message: 'حدث خطأ أثناء الحذف من المفضلة' };
    }
  };

  // Toggle favorite status
  const toggleFavorite = async (productId) => {
    if (favorites.has(productId)) {
      return await removeFromFavorites(productId);
    } else {
      return await addToFavorites(productId);
    }
  };

  // Check if product is favorited
  const isFavorite = (productId) => {
    return favorites.has(productId);
  };

  // Get all favorite products with full details
  const getFavoriteProducts = async () => {
    if (!user || favorites.size === 0) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          categories(name),
          product_colors(
            id,
            color,
            colors(name),
            product_color_images(image_url),
            product_color_sizes(size, price, stock)
          )
        `)
        .in('id', Array.from(favorites));

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching favorite products:', error);
      return [];
    }
  };

  const value = {
    favorites,
    loading,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    isFavorite,
    getFavoriteProducts,
    loadFavorites
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
